/**
 * How a skill's file is shown (ADR-216 Amendment 1): Markdown rendered as the
 * repository docs are, with SKILL.md's frontmatter as a header; scripts and
 * data highlighted by extension; everything with line numbers in Source. The
 * editor reuses the same highlighting under a transparent textarea.
 */
import { useCallback, useMemo, useRef, useState, type MouseEvent } from 'react';
import hljs from 'highlight.js';
import { Check, Copy, Link2 } from 'lucide-react';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { parseFrontMatter } from '@/lib/frontMatter';
import { cn } from '@/lib/utils';
import '@/styles/docs-shared.css';
import { isMarkdown, languageFor, linesOf, resolveLink, type SkillFileText } from './skillFiles';

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Highlighted HTML for a file. highlight.js escapes the text it is given. */
export function highlightFile(path: string, content: string): string {
  const language = languageFor(path);
  if (language && hljs.getLanguage(language)) {
    return hljs.highlight(content, { language, ignoreIllegals: true }).value;
  }
  return escapeHtml(content);
}

const CODE_BOX = 'hljs font-mono text-xs leading-5';

/** A file's text, highlighted, with line numbers. */
export function CodeView({ path, content, testId }: { path: string; content: string; testId?: string }) {
  const html = useMemo(() => highlightFile(path, content), [path, content]);
  const count = linesOf(content).length;
  return (
    <div
      className={cn(CODE_BOX, 'flex max-h-[32rem] overflow-auto rounded-lg border border-slate-200 dark:border-slate-800')}
      data-testid={testId}
      data-language={languageFor(path) ?? 'plain'}
    >
      <pre aria-hidden className="m-0 select-none py-3 pr-3 pl-3 text-right text-slate-500" data-testid="skill-line-numbers">
        {Array.from({ length: count }, (_, i) => i + 1).join('\n')}
      </pre>
      <pre className="m-0 flex-1 whitespace-pre py-3 pr-4">
        <code dangerouslySetInnerHTML={{ __html: html }} />
      </pre>
    </div>
  );
}

/**
 * A plain textarea over the highlighted text: what you type is what is
 * published, and the colours follow. `errorLine` marks the line a refused
 * publish named.
 */
export function CodeEditor({
  path,
  value,
  onChange,
  errorLine,
}: {
  path: string;
  value: string;
  onChange: (value: string) => void;
  errorLine?: number;
}) {
  const highlighted = useRef<HTMLPreElement>(null);
  const gutter = useRef<HTMLDivElement>(null);
  // A trailing newline keeps the last, empty line as tall as the textarea's.
  const html = useMemo(() => highlightFile(path, `${value}\n`), [path, value]);
  const count = Math.max(1, value.split('\n').length);
  const sync = (e: React.UIEvent<HTMLTextAreaElement>) => {
    const { scrollTop, scrollLeft } = e.currentTarget;
    if (highlighted.current) {
      highlighted.current.scrollTop = scrollTop;
      highlighted.current.scrollLeft = scrollLeft;
    }
    if (gutter.current) gutter.current.scrollTop = scrollTop;
  };
  return (
    <div className={cn(CODE_BOX, 'flex h-[32rem] overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800')}>
      <div ref={gutter} aria-hidden className="select-none overflow-hidden py-3 pr-2 pl-3 text-right text-slate-500">
        {Array.from({ length: count }, (_, i) => (
          <div
            key={i}
            className={cn(i + 1 === errorLine && 'bg-red-600 text-white')}
            data-testid={i + 1 === errorLine ? 'skill-editor-error-line' : undefined}
          >
            {i + 1}
          </div>
        ))}
      </div>
      <div className="relative flex-1">
        <pre
          ref={highlighted}
          aria-hidden
          className="pointer-events-none absolute inset-0 m-0 overflow-hidden whitespace-pre p-3"
        >
          <code dangerouslySetInnerHTML={{ __html: html }} />
        </pre>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onScroll={sync}
          spellCheck={false}
          wrap="off"
          aria-label={`Edit ${path}`}
          data-testid="skill-editor-text"
          className="absolute inset-0 m-0 resize-none overflow-auto whitespace-pre border-0 bg-transparent p-3 font-mono text-xs leading-5 text-transparent caret-white outline-none"
        />
      </div>
    </div>
  );
}

/** SKILL.md's frontmatter as a header, not as raw YAML. */
function FrontMatterHeader({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return null;
  return (
    <dl
      className="mb-4 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs dark:border-slate-800 dark:bg-slate-900"
      data-testid="skill-frontmatter"
    >
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="font-mono text-slate-500">{key}</dt>
          <dd className="text-slate-800 dark:text-slate-200">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A file rendered as Markdown; links to other files of the skill open them here. */
export function MarkdownFile({
  file,
  paths,
  onOpen,
}: {
  file: SkillFileText;
  paths: readonly string[];
  onOpen: (path: string) => void;
}) {
  const { data } = useMemo(() => parseFrontMatter(file.content), [file.content]);
  const open = useCallback(
    (href: string) => {
      const target = resolveLink(file.path, href, paths);
      if (target) onOpen(target);
    },
    [file.path, paths, onOpen],
  );
  // MarkdownRenderer turns only .md links into in-page links; a link to a
  // script would otherwise send the browser to a URL that does not exist.
  function onClickCapture(e: MouseEvent<HTMLDivElement>) {
    const anchor = (e.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');
    if (!anchor || !href || anchor.dataset.docLink || href.startsWith('#')) return;
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('/')) return;
    e.preventDefault();
    open(href);
  }
  return (
    // docs-page + article: the shared doc typography the book reader renders with.
    <div className="docs-page" onClickCapture={onClickCapture}>
      {file.path === 'SKILL.md' && <FrontMatterHeader data={data} />}
      <article>
        <MarkdownRenderer content={file.content} basePath="skill" onNavigate={open} />
      </article>
    </div>
  );
}

function SmallCopy({ text, label, icon: Icon, testId }: { text: string; label: string; icon: typeof Copy; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          setCopied(false);
        }
      }}
      className="flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

/** One file of a version: Rendered | Source for Markdown, highlighted source otherwise. */
export function SkillFileView({
  file,
  paths,
  onOpen,
  link,
}: {
  file: SkillFileText;
  paths: readonly string[];
  onOpen: (path: string) => void;
  /** This file's shareable address. */
  link: string;
}) {
  const markdown = isMarkdown(file.path);
  const [source, setSource] = useState(false);
  return (
    <div className="min-w-0">
      <div className="mb-2 flex flex-wrap items-center gap-1">
        <span className="mr-auto truncate font-mono text-xs text-slate-500">{file.path}</span>
        {markdown && (
          <div className="flex rounded-md border border-slate-200 text-xs dark:border-slate-700" role="group">
            {(['Rendered', 'Source'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                data-testid={`skill-view-${mode.toLowerCase()}`}
                aria-pressed={(mode === 'Source') === source}
                onClick={() => setSource(mode === 'Source')}
                className={cn(
                  'px-2 py-1',
                  (mode === 'Source') === source
                    ? 'bg-slate-100 font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100'
                    : 'text-slate-500',
                )}
              >
                {mode}
              </button>
            ))}
          </div>
        )}
        <SmallCopy text={file.content} label="Copy" icon={Copy} testId="skill-file-copy" />
        <SmallCopy text={link} label="Link" icon={Link2} testId="skill-file-link" />
      </div>
      {markdown && !source ? (
        <div
          className="max-h-[40rem] overflow-auto rounded-lg border border-slate-200 bg-white p-4 sm:p-6 dark:border-slate-800 dark:bg-slate-900"
          data-testid="skill-file-content"
        >
          <MarkdownFile file={file} paths={paths} onOpen={onOpen} />
        </div>
      ) : (
        <CodeView path={file.path} content={file.content} testId="skill-file-source" />
      )}
    </div>
  );
}
